// Shared helpers for API-delivered PDFs (base64 in a JSON payload).
//
// Both the V2 contract endpoints and the Other Services document endpoints
// (clearance letter today, debt-proof letter later) return the file as a base64
// string rather than a binary download, so every consumer needs the same
// decode → Blob → object-URL dance. Centralised here so the decoding rules and
// the cleanup contract stay in one place.
//
// IMPORTANT: every URL produced by base64ToPdf() must be handed to
// revokePdfUrl() when the component unmounts or replaces it — object URLs are
// held by the document until explicitly revoked.

/**
 * Decode a base64 PDF into a Blob + object URL.
 * @param {string} base64 - raw base64 (data-URI prefixes are tolerated)
 * @returns {{ url: string, size: number }|null} null when the input is unusable
 */
export const base64ToPdf = (base64) => {
  if (!base64 || typeof base64 !== "string") return null;

  // Tolerate a `data:application/pdf;base64,` prefix and stray whitespace —
  // Temenos sends the bare string today, but the contract endpoints have been
  // seen wrapping it.
  const cleaned = base64.includes(",") ? base64.split(",").pop() : base64;

  try {
    const byteCharacters = atob(cleaned.replace(/\s/g, ""));
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
    return { url: URL.createObjectURL(blob), size: blob.size };
  } catch (error) {
    // Malformed base64 — treat as "no document" rather than crashing the flow.
    console.error("Failed to decode PDF payload:", error);
    return null;
  }
};

/** Release an object URL created by base64ToPdf(). Safe to call with null. */
export const revokePdfUrl = (url) => {
  if (url) URL.revokeObjectURL(url);
};

/** Open a decoded document in a new tab (the "view" action). */
export const openPdf = (url) => {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Trigger a browser download for a decoded document.
 * Uses a detached anchor so no visible link is needed in the DOM.
 */
export const downloadPdf = (url, fileName) => {
  if (!url) return;
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "document.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/** Human-readable file size, e.g. 259_800 → "254 KB". */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};
